'use client';

import React, { useCallback } from 'react';
import { Cart } from 'shared/types/cart';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { CloudinaryProductAssets, ContentfulProduct } from 'shared/types/content';
import { ExtendedProductDetailProduct } from 'shared/types/product/Product';
import { Variant } from 'shared/types/product/Variant';
import CustomProductDetailsAdapter from 'components/commercetools-ui/organisms/product/custom-product-details/helpers/adapter';
import { ProductDetailsProps } from 'components/commercetools-ui/organisms/product/product-details';
import { hasCustomQuoteCta } from 'components/commercetools-ui/organisms/product/product-details/helpers';
import ProductDetailsAdapter from 'components/commercetools-ui/organisms/product/product-details/helpers/adapter';
import { useProductTracking } from 'helpers/hooks/useProductTracking';
import { ugcCableSystemForCategory, ugcProductForCategory } from 'helpers/ugc';
import { useCart, useGTM } from 'frontastic/hooks';
import Breadcrumb, { Crumb } from './breadcrumb';
import UgcGallery from './ugc-gallery';
import VariantLinks from './variant-links';

interface BrandPdpProps {
  product: ExtendedProductDetailProduct;
  content?: ContentfulProduct;
  assets?: CloudinaryProductAssets;
  /** Studio content from the product-details tastic; without it the Wirebrushed option does not render. */
  wirebrushModal?: ProductDetailsProps['wirebrushModal'];
  woodFinishHelper?: ProductDetailsProps['woodFinishHelper'];
  breadcrumbs: Crumb[];
  /** Server's device guess: render the gallery's phone layout first. */
  phoneLayout?: boolean;
  /** Request origin, so the structured data can publish absolute variant URLs. */
  origin?: string;
  /** The product's assigned lifestyle photo (`feed_lifestyle:<slug>`), added to the structured data. */
  feedLifestyleImage?: string;
  /** The Related Products row, rendered (and streamed) by the server page. */
  relatedProducts?: React.ReactNode;
  /** The SKU this page is for, so the variant links can leave it out. */
  currentSku?: string;
}

// The PDP content inside the brand-refresh chrome (BrandLayout supplies the header + footer). It's
// the existing product-details adapter with its built-in breadcrumb suppressed in favour of the
// brand-refresh breadcrumb rendered here. Custom-quote products (FLIGHT / floating stairs) use the
// custom adapter, mirroring the product-details tastic. Cart wiring and analytics mirror the tastic too.
const BrandPdp = ({
  product,
  content,
  assets,
  wirebrushModal,
  woodFinishHelper,
  breadcrumbs,
  origin,
  feedLifestyleImage,
  relatedProducts,
  currentSku,
  phoneLayout,
}: BrandPdpProps) => {
  const { addItem, data: cart, removeItem, updateItem } = useCart();
  const { viewItemEvent, addToCartEvent } = useGTM();

  useProductTracking({ product, onProductView: viewItemEvent });

  const handleAddToCart = useCallback(
    async (
      variant: Variant,
      quantity: number,
      finish?: string,
      sheen?: string,
      hasWirebrush?: boolean,
      // The custom adapter adds several lines for one product and reports them itself.
      options?: { skipAnalytics?: boolean },
    ): Promise<Cart> => {
      const updatedCart = await addItem(variant, quantity, finish, sheen, hasWirebrush);
      if (updatedCart?.cartId && !options?.skipAnalytics) addToCartEvent(updatedCart);
      return updatedCart;
    },
    [addItem, addToCartEvent],
  );

  // Crumbs name the category path (e.g. Railing / Signature Cable Railing / Posts), which maps to the
  // project photos for that system; categories with no photos (treads, handrail) skip the gallery.
  const ugcProduct = ugcProductForCategory(...breadcrumbs.map((crumb) => crumb.label));
  // The section heading names the most specific linked category that maps to the same photos
  // ("Express Cable Railing"), skipping crumbs like "Railing Posts" and the product's own crumb.
  // Cable PDPs narrow to their system (Signature / Express / DriveTite), from the same crumbs.
  const ugcSystem = ugcProduct === 'cable-railing' ? ugcCableSystemForCategory(...breadcrumbs.map((crumb) => crumb.label)) : undefined;
  const ugcSubject = [...breadcrumbs].reverse().find((crumb) => crumb.href && ugcProduct && ugcProductForCategory(crumb.label) === ugcProduct)?.label;

  const Adapter = hasCustomQuoteCta(product.categories) ? CustomProductDetailsAdapter : ProductDetailsAdapter;

  return (
    <>
      {/* Same `.container` as the PDP wrapper so the breadcrumb's left edge lines up with the
          leftmost gallery image. */}
      <div className="container pt-24">
        <Breadcrumb crumbs={breadcrumbs} />
      </div>

      <Adapter
        {...(Adapter === ProductDetailsAdapter ? { phoneLayout } : {})}
        product={product}
        content={content}
        assets={assets}
        origin={origin}
        feedLifestyleImage={feedLifestyleImage}
        cart={cart}
        onAddToCart={handleAddToCart}
        wirebrushModal={wirebrushModal}
        woodFinishHelper={woodFinishHelper}
        removeLineItem={(item) => removeItem(item.lineItemId as string, (item as ExtendedLineItem).customLineItemId)}
        updateLineItem={updateItem}
      />

      {currentSku && <VariantLinks product={product} currentSku={currentSku} />}
      {ugcProduct && <UgcGallery product={ugcProduct} system={ugcSystem} subject={ugcSubject} />}
      {relatedProducts}
    </>
  );
};

export default BrandPdp;
